import type { DesktopUpdateState } from "@cmmd-forge/contracts";
import { DownloadIcon, InfoIcon, RotateCwIcon, TriangleAlertIcon, XIcon } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useId, useState } from "react";
import { flushSync } from "react-dom";
import { isElectron } from "../../env";
import {
  setDesktopUpdateStateQueryData,
  useDesktopUpdateState,
} from "../../lib/desktopUpdateReactQuery";
import { toastManager } from "../ui/toast";
import {
  getArm64IntelBuildWarningDescription,
  getDesktopUpdateActionError,
  getDesktopUpdateButtonTooltip,
  getDesktopUpdateInstallConfirmationMessage,
  isDesktopUpdateButtonDisabled,
  resolveDesktopUpdateButtonAction,
  shouldShowArm64IntelBuildWarning,
  shouldShowDesktopUpdateButton,
  shouldToastDesktopUpdateActionResult,
} from "../desktopUpdate.logic";
import { Alert, AlertDescription, AlertTitle } from "../ui/alert";
import { Popover, PopoverCreateHandle, PopoverPopup, PopoverTrigger } from "../ui/popover";
import { Tooltip, TooltipPopup, TooltipTrigger } from "../ui/tooltip";
import { ensureLocalApi } from "../../localApi";
import { SidebarUpdateReleaseNotes, type DesktopUpdateShell } from "./SidebarUpdateReleaseNotes";

/**
 * The desktop shell, only when it can actually open a URL. Guards the type
 * instead of casting: a bridge without `openExternal` renders a popover
 * whose links degrade to the failure toast rather than throwing.
 */
function resolveDesktopUpdateShell(): DesktopUpdateShell | undefined {
  const bridge = window.desktopBridge;
  return typeof bridge?.openExternal === "function" ? bridge : undefined;
}

/**
 * The nightly release-notes popover is available exactly when the pill has an
 * update to act on and the channel carries notes: forge's stable channel has
 * no per-release notes, and a nightly with nothing pending has nothing to
 * announce. Unlike upstream, visibility follows forge's own pill model, where
 * a freshly available update stays quiet until it is downloading or done.
 */
export function shouldUseSidebarUpdateReleaseNotesPopover(
  state: DesktopUpdateState | null,
): boolean {
  return (
    state !== null &&
    state.channel === "nightly" &&
    state.releaseNotes.length > 0 &&
    shouldShowDesktopUpdateButton(state)
  );
}

/** Promote a hover-open popover before native Tab runs, so focus follows. */
export function openSidebarUpdateReleaseNotesPopoverOnForwardTab(
  event: { readonly key: string; readonly shiftKey: boolean },
  handle: { open: (id: string) => void },
  triggerId: string,
): void {
  if (event.key !== "Tab" || event.shiftKey) return;
  flushSync(() => handle.open(triggerId));
}

/**
 * The Apple Silicon warning, its own renderer: the pill renders at most one
 * alert, and the warning's visibility rules are its whole job.
 */
function SidebarUpdateArchitectureAlert(props: {
  readonly state: DesktopUpdateState | null;
}): React.ReactNode | null {
  const visible = shouldShowArm64IntelBuildWarning(props.state);
  const description =
    props.state && visible ? getArm64IntelBuildWarningDescription(props.state) : null;

  if (!visible || !description) return null;

  return (
    <Alert
      variant="warning"
      className="rounded-2xl border-warning-border bg-warning-surface text-xs"
    >
      <TriangleAlertIcon />
      <AlertTitle>Intel build on Apple Silicon</AlertTitle>
      <AlertDescription>{description}</AlertDescription>
    </Alert>
  );
}

/**
 * The nightly release-notes popover and its info trigger. Owns the hover and
 * focus-open wiring; the parent only decides whether it renders at all.
 */
function SidebarUpdateReleaseNotesTrigger(props: {
  readonly handle: ReturnType<typeof PopoverCreateHandle>;
  readonly state: DesktopUpdateState;
  readonly tooltip: string;
  readonly triggerId: string;
}): React.ReactNode {
  return (
    <Popover handle={props.handle}>
      <Tooltip>
        <TooltipTrigger
          id={props.triggerId}
          render={
            <PopoverTrigger
              aria-label="Release notes"
              closeDelay={150}
              handle={props.handle}
              id={props.triggerId}
              openOnHover
              render={
                <button
                  className="mr-1 inline-flex size-8 items-center justify-center rounded-[var(--control-radius)] text-primary/60 transition-colors hover:bg-sidebar-row-hover hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  onClick={() => props.handle.open(props.triggerId)}
                  onKeyDown={(event) => {
                    openSidebarUpdateReleaseNotesPopoverOnForwardTab(
                      event.nativeEvent,
                      props.handle,
                      props.triggerId,
                    );
                  }}
                  type="button"
                >
                  <InfoIcon className="size-3.5" />
                </button>
              }
            />
          }
        />
        <TooltipPopup side="top">Release notes</TooltipPopup>
      </Tooltip>
      <PopoverPopup
        align="center"
        aria-label="Nightly update release notes"
        initialFocus={false}
        side="top"
        tooltipStyle
      >
        <SidebarUpdateReleaseNotes
          shell={resolveDesktopUpdateShell()}
          state={props.state}
          tooltip={props.tooltip}
        />
      </PopoverPopup>
    </Popover>
  );
}

export function SidebarUpdatePill() {
  const queryClient = useQueryClient();
  const state = useDesktopUpdateState().data ?? null;
  const [dismissed, setDismissed] = useState(false);
  const [releaseNotesPopoverHandle] = useState(() => PopoverCreateHandle());
  const releaseNotesTriggerId = useId();

  const visible = isElectron && shouldShowDesktopUpdateButton(state) && !dismissed;
  const tooltip = state ? getDesktopUpdateButtonTooltip(state) : "Update available";
  const disabled = isDesktopUpdateButtonDisabled(state);
  const action = state ? resolveDesktopUpdateButtonAction(state) : "none";
  const showReleaseNotesPopover = shouldUseSidebarUpdateReleaseNotesPopover(state);

  const handleAction = useCallback(() => {
    const bridge = window.desktopBridge;
    if (!bridge || !state) return;
    if (disabled || action === "none") return;

    if (action === "download") {
      void bridge
        .downloadUpdate()
        .then((result) => {
          setDesktopUpdateStateQueryData(queryClient, result.state);
          if (result.completed) {
            toastManager.add({
              type: "success",
              title: "Update downloaded",
              description: "Restart the app from the update button to install it.",
            });
          }
          if (!shouldToastDesktopUpdateActionResult(result)) return;
          const actionError = getDesktopUpdateActionError(result);
          if (!actionError) return;
          toastManager.add({
            type: "error",
            title: "Could not download update",
            description: actionError,
          });
        })
        .catch((error) => {
          toastManager.add({
            type: "error",
            title: "Could not start update download",
            description: error instanceof Error ? error.message : "An unexpected error occurred.",
          });
        });
      return;
    }

    if (action === "install") {
      void ensureLocalApi()
        .dialogs.confirm(getDesktopUpdateInstallConfirmationMessage(state))
        .then((confirmed) => {
          if (!confirmed) return null;
          return bridge.installUpdate().then((result) => {
            setDesktopUpdateStateQueryData(queryClient, result.state);
            if (!shouldToastDesktopUpdateActionResult(result)) return;
            const actionError = getDesktopUpdateActionError(result);
            if (!actionError) return;
            toastManager.add({
              type: "error",
              title: "Could not install update",
              description: actionError,
            });
          });
        })
        .catch((error) => {
          toastManager.add({
            type: "error",
            title: "Could not install update",
            description: error instanceof Error ? error.message : "An unexpected error occurred.",
          });
        });
    }
  }, [action, disabled, queryClient, state]);

  const showArm64Warning = isElectron && shouldShowArm64IntelBuildWarning(state);

  if (!visible && !showArm64Warning) return null;

  return (
    <div className="flex flex-col gap-1 px-2 pb-2">
      <SidebarUpdateArchitectureAlert state={state} />
      {visible ? (
        <div
          className={`group/update relative flex h-7 w-full items-center rounded-lg bg-primary/15 text-xs font-medium text-primary ${
            disabled ? " cursor-not-allowed opacity-60" : ""
          }`}
        >
          <div className="pointer-events-none absolute inset-0 rounded-lg transition-colors group-has-[button.update-main:hover]/update:bg-primary/22" />
          <Tooltip>
            <TooltipTrigger
              render={
                <button
                  type="button"
                  aria-label={tooltip}
                  aria-disabled={disabled || undefined}
                  disabled={disabled}
                  className="update-main relative flex h-full flex-1 items-center gap-2 px-2 enabled:cursor-pointer"
                  onClick={handleAction}
                >
                  {action === "install" ? (
                    <>
                      <RotateCwIcon className="size-3.5" />
                      <span>Restart to update</span>
                    </>
                  ) : state?.status === "downloading" ? (
                    <>
                      <DownloadIcon className="size-3.5" />
                      <span>
                        Downloading
                        {typeof state.downloadPercent === "number"
                          ? ` (${Math.floor(state.downloadPercent)}%)`
                          : "…"}
                      </span>
                    </>
                  ) : (
                    <>
                      <DownloadIcon className="size-3.5" />
                      <span>Retry download</span>
                    </>
                  )}
                </button>
              }
            />
            <TooltipPopup side="top">{tooltip}</TooltipPopup>
          </Tooltip>
          {showReleaseNotesPopover && state ? (
            <SidebarUpdateReleaseNotesTrigger
              handle={releaseNotesPopoverHandle}
              state={state}
              tooltip={tooltip}
              triggerId={releaseNotesTriggerId}
            />
          ) : null}
          {action === "download" ? (
            <Tooltip>
              <TooltipTrigger
                render={
                  <button
                    type="button"
                    aria-label="Dismiss update"
                    className="mr-1 inline-flex size-5 items-center justify-center rounded-md text-primary/60 transition-colors hover:text-primary"
                    onClick={() => setDismissed(true)}
                  >
                    <XIcon className="size-3.5" />
                  </button>
                }
              />
              <TooltipPopup side="top">Dismiss until next launch</TooltipPopup>
            </Tooltip>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
